/**
 * 文案库页面 — CopyLibrary（二期批2 翻新，对齐概念稿 copy-library.html）。
 *
 * GET /api/delivery-copies?product_id&company_id&status   文案列表（选择器过滤）
 * GET /api/products/{id}/problem-matches                  场景列表（选中产品时解析关联场景名）
 *
 * 结构：4 指标卡 → g38（状态分布 + 产出说明）→ 工具栏（状态桶）→ 文案表 → 详情弹窗。
 * PRD §3.1 双向关联：文案存 related_scenario_ids（problem_matches.id 列表），
 * 详情弹窗展示关联场景；状态机与 AI 优化建议均为接口真实字段。
 *
 * 概念稿裁剪：文案「类型分布」（产品稿/场景稿/问答稿/口碑稿）无接口字段，
 * 以「状态分布」替代，不虚构类型；质检报告（事实命中率/合规检查）属二期续建。
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { confirmDeliveryCopy, getDeliveryCopies } from "../api/copies";
import { getProblemMatches } from "../api/products";
import { useCompanyStore } from "../store/companyStore";
import { COPY_STATUS_MAP } from "../utils/constants";
import { formatDateTime } from "../utils/format";
import type { DeliveryCopy, ProblemMatch } from "../types";

/** 状态桶徽标。 */
function BucketChip({
  label,
  count,
  active,
  color,
  bg,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  color: string;
  bg: string;
  onClick: () => void;
}) {
  return (
    <Chip
      label={`${label} ${count}`}
      size="small"
      onClick={onClick}
      sx={{
        fontSize: 11,
        height: 22,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? color : "text.secondary",
        bgcolor: active ? bg : "semanticBg.neutral",
        border: "1px solid",
        borderColor: active ? color : "transparent",
      }}
    />
  );
}

/** 文案状态徽章（未命中回退灰）。 */
function CopyStatusBadge({ status }: { status: string }) {
  const info = COPY_STATUS_MAP[status] || {
    label: status,
    color: "text.secondary",
    bg: "semanticBg.neutral",
  };
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

export default function CopyLibrary() {
  const selectedCompanyId = useCompanyStore((s) => s.selectedCompanyId);
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  const [copies, setCopies] = useState<DeliveryCopy[]>([]);
  const [matches, setMatches] = useState<ProblemMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detailCopy, setDetailCopy] = useState<DeliveryCopy | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  /** 文案列表（选择器过滤：产品优先，其次公司）。 */
  const loadCopies = () => {
    setLoading(true);
    setError("");
    getDeliveryCopies({
      product_id: selectedProductId || undefined,
      company_id: selectedProductId ? undefined : selectedCompanyId || undefined,
    })
      .then(setCopies)
      .catch((err) => {
        setError(err instanceof Error ? err.message : "获取文案数据失败");
        setCopies([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCopies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, selectedProductId]);

  /** 确认文案（pending_confirm → confirmed）后刷新列表；详情弹窗中确认则关闭弹窗。 */
  const handleConfirm = (copy: DeliveryCopy) => {
    setConfirmingId(copy.id);
    confirmDeliveryCopy(copy.id)
      .then(() => {
        if (detailCopy?.id === copy.id) setDetailCopy(null);
        loadCopies();
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : "确认失败，请重试"),
      )
      .finally(() => setConfirmingId(null));
  };

  /** 场景列表：仅在选中产品时拉取（problem-matches 为产品级端点）。 */
  useEffect(() => {
    if (!selectedProductId) {
      setMatches([]);
      return;
    }
    getProblemMatches(selectedProductId)
      .then(setMatches)
      .catch(() => setMatches([]));
  }, [selectedProductId]);

  /** 场景 ID → 场景对象映射。 */
  const matchMap = useMemo(() => {
    const map = new Map<string, ProblemMatch>();
    for (const m of matches) map.set(m.id, m);
    return map;
  }, [matches]);

  /** 获取文案关联的场景列表（能解析出名字的）。 */
  const getRelatedScenarios = (copy: DeliveryCopy): ProblemMatch[] => {
    if (!copy.related_scenario_ids || !Array.isArray(copy.related_scenario_ids))
      return [];
    return copy.related_scenario_ids
      .map((id) => matchMap.get(id))
      .filter((m): m is ProblemMatch => m !== undefined);
  };

  /** 关联场景数（含未解析出名字的）。 */
  const relatedCount = (copy: DeliveryCopy): number =>
    Array.isArray(copy.related_scenario_ids) ? copy.related_scenario_ids.length : 0;

  /** 状态统计。 */
  const statusCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of copies) map.set(c.status, (map.get(c.status) || 0) + 1);
    return map;
  }, [copies]);

  /** 本月新增。 */
  const monthNew = useMemo(() => {
    const now = new Date();
    return copies.filter((c) => {
      const d = new Date(c.generated_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  }, [copies]);

  /** 状态分布（分布卡数据源）。 */
  const statusDist = useMemo(() => {
    const order = ["pending_confirm", "confirmed", "used", "draft", "archived"];
    const arr: { status: string; count: number; info: { label: string } }[] = order
      .filter((s) => statusCounts.has(s))
      .map((s) => ({
        status: s,
        count: statusCounts.get(s) || 0,
        info: COPY_STATUS_MAP[s] || { label: s },
      }));
    // 未知状态兜底
    for (const [s, n] of statusCounts) {
      if (!order.includes(s)) arr.push({ status: s, count: n, info: { label: s } });
    }
    const max = arr.length > 0 ? Math.max(...arr.map((a) => a.count)) : 1;
    return { arr, max };
  }, [statusCounts]);

  const filtered = useMemo(
    () => (statusFilter ? copies.filter((c) => c.status === statusFilter) : copies),
    [copies, statusFilter],
  );

  const columns: DataTableColumn<DeliveryCopy>[] = [
    {
      key: "title",
      label: "标题",
      render: (row) => (
        <Typography sx={{ fontSize: 13, fontWeight: 500, whiteSpace: "normal" }}>
          {row.title}
        </Typography>
      ),
    },
    {
      key: "delivery_media",
      label: "投放媒体（投放库）",
      render: (row) =>
        row.delivery_media_names && row.delivery_media_names.length > 0 ? (
          <Typography sx={{ fontSize: 13 }}>
            {row.delivery_media_names.join(" · ")}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
            {row.target_media || "—"}
          </Typography>
        ),
    },
    {
      key: "words",
      label: "字数",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {row.content_body ? row.content_body.length.toLocaleString() : "—"}
        </Typography>
      ),
    },
    {
      key: "scenes",
      label: "关联场景",
      align: "center",
      render: (row) =>
        relatedCount(row) > 0 ? (
          <Badge
            label={`${relatedCount(row)} 个场景`}
            color="primary.main"
            bgColor="brand.50"
          />
        ) : (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
        ),
    },
    {
      key: "status",
      label: "状态",
      align: "center",
      render: (row) => <CopyStatusBadge status={row.status} />,
    },
    {
      key: "generated_at",
      label: "生成时间",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDateTime(row.generated_at)}
        </Typography>
      ),
    },
    {
      key: "action",
      label: "操作",
      align: "center",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <Button size="small" variant="text" onClick={() => setDetailCopy(row)}>
            查看
          </Button>
          <Button
            size="small"
            variant="text"
            component="a"
            href="/delivery-plans"
            onClick={(e) => e.stopPropagation()}
          >
            看计划
          </Button>
          {row.status === "pending_confirm" && (
            <Button
              size="small"
              variant="text"
              disabled={confirmingId === row.id}
              onClick={() => handleConfirm(row)}
            >
              确认
            </Button>
          )}
        </Box>
      ),
    },
  ];

  const detailScenarios = detailCopy ? getRelatedScenarios(detailCopy) : [];
  const detailRelatedCount = detailCopy ? relatedCount(detailCopy) : 0;

  return (
    <Box>
      <PageHeader
        title="文案库"
        breadcrumbs={[{ label: "媒体投放" }, { label: "文案库" }]}
        subtitle="内容生成 skill 产出的投放文案资产 · 按状态检索"
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="文案总数"
            value={loading ? "—" : copies.length}
            subtitle={
              copies.length > 0
                ? `已投放 ${statusCounts.get("used") || 0} · 待确认 ${statusCounts.get("pending_confirm") || 0}`
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="本月新增"
            value={loading ? "—" : monthNew}
            subtitle="内容生成 skill 产出"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="已投放"
            value={loading ? "—" : statusCounts.get("used") || 0}
            subtitle="收录进展见发文记录 / 收录台账"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="待确认"
            value={loading ? "—" : statusCounts.get("pending_confirm") || 0}
            subtitle="待人工确认后进入投放"
          />
        </Grid>
      </Grid>

      {/* g38：状态分布 + 产出说明 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h4">状态分布</Typography>
                <Badge label={`${statusDist.arr.length} 类`} />
              </Box>
              {loading ? (
                <Loading text="加载中..." />
              ) : statusDist.arr.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                  暂无文案数据
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {statusDist.arr.map((s) => (
                    <Box key={s.status} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 12, minWidth: 56, color: "text.secondary" }}>
                        {s.info.label}
                      </Typography>
                      <Box
                        sx={{
                          flexGrow: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: "semanticBg.neutral",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(s.count / statusDist.max) * 100}%`,
                            height: "100%",
                            bgcolor: "primary.main",
                            borderRadius: 3,
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 28, textAlign: "right" }}>
                        {s.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4">产出与质检说明</Typography>
                <Badge label="网站只读" variant="info" />
              </Box>
              <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                文案由内容生成 skill 基于事实包自动产出，经 Agent API 入库；
                网站侧只承接查看，不承接生成与改写。
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
                质检报告展示（事实命中率、合规检查结果）属二期续建范围，当前以状态徽章
                区分流转阶段；已投放文案可在「发文记录」查看收录进展；关联场景来自
                PRD §3.1 双向关联（related_scenario_ids）。
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 工具栏：状态桶 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <BucketChip
              label="全部"
              count={copies.length}
              active={statusFilter === ""}
              color="primary.main"
              bg="brand.50"
              onClick={() => setStatusFilter("")}
            />
            <BucketChip
              label="待确认"
              count={statusCounts.get("pending_confirm") || 0}
              active={statusFilter === "pending_confirm"}
              color="warning.main"
              bg="semanticBg.warning"
              onClick={() => setStatusFilter("pending_confirm")}
            />
            <BucketChip
              label="已确认"
              count={statusCounts.get("confirmed") || 0}
              active={statusFilter === "confirmed"}
              color="info.main"
              bg="semanticBg.info"
              onClick={() => setStatusFilter("confirmed")}
            />
            <BucketChip
              label="已投放"
              count={statusCounts.get("used") || 0}
              active={statusFilter === "used"}
              color="success.main"
              bg="semanticBg.success"
              onClick={() => setStatusFilter("used")}
            />
            <BucketChip
              label="草稿"
              count={statusCounts.get("draft") || 0}
              active={statusFilter === "draft"}
              color="text.secondary"
              bg="semanticBg.neutral"
              onClick={() => setStatusFilter("draft")}
            />
            <BucketChip
              label="已归档"
              count={statusCounts.get("archived") || 0}
              active={statusFilter === "archived"}
              color="text.secondary"
              bg="semanticBg.neutral"
              onClick={() => setStatusFilter("archived")}
            />
          </Box>
        </CardContent>
      </Card>

      {/* 文案表 */}
      {loading ? (
        <Loading fullPage />
      ) : error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : filtered.length === 0 && copies.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              text="暂无文案数据"
              source="由内容生成 skill 产出 · 产出后自动展示"
            />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          showPagination={false}
          getRowId={(row) => row.id}
          emptyText="该状态桶暂无文案"
        />
      )}

      {/* 文案详情弹窗 */}
      <Dialog
        open={detailCopy !== null}
        onClose={() => setDetailCopy(null)}
        maxWidth="md"
        fullWidth
      >
        {detailCopy && (
          <>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ flexGrow: 1 }}>
                  {detailCopy.title}
                </Box>
                <CopyStatusBadge status={detailCopy.status} />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    投放媒体（投放库）
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {detailCopy.delivery_media_names &&
                    detailCopy.delivery_media_names.length > 0
                      ? detailCopy.delivery_media_names.join(" · ")
                      : detailCopy.target_media || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                    生成时间
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {formatDateTime(detailCopy.generated_at)}
                  </Typography>
                </Grid>
              </Grid>

              {detailCopy.core_selling_points && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 0.5 }}
                  >
                    核心卖点
                  </Typography>
                  <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                    {detailCopy.core_selling_points}
                  </Typography>
                </Box>
              )}

              {detailCopy.ai_optimization_advice && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 0.5 }}
                  >
                    AI 优化建议
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "pre-wrap" }}>
                    {detailCopy.ai_optimization_advice}
                  </Typography>
                </Box>
              )}

              {detailCopy.content_body && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 0.5 }}
                  >
                    内容正文（{detailCopy.content_body.length.toLocaleString()} 字）
                  </Typography>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "brand.50",
                      borderRadius: 1,
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                      {detailCopy.content_body}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* 关联场景（PRD §3.1 双向关联） */}
              <Box>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 500, color: "primary.main", mb: 1 }}
                >
                  关联场景（{detailRelatedCount} 个）
                </Typography>
                {detailScenarios.length > 0 ? (
                  detailScenarios.map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "brand.50",
                        borderLeft: "3px solid",
                        borderColor: "primary.main",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 600, color: "primary.main", mb: 0.5 }}
                      >
                        {m.scenario}
                      </Typography>
                      {m.customer_desc && (
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          客户：{m.customer_desc}
                        </Typography>
                      )}
                      {m.product_match && (
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          匹配：{m.product_match}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : detailRelatedCount > 0 ? (
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    关联了 {detailRelatedCount} 个场景；选中对应产品后可解析场景名。
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                    未关联场景
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setDetailCopy(null)} color="inherit">
                关闭
              </Button>
              {detailCopy.status === "pending_confirm" && (
                <Button
                  variant="contained"
                  disabled={confirmingId === detailCopy.id}
                  onClick={() => handleConfirm(detailCopy)}
                >
                  确认入库
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
